// src/theme/components/input.js
import { inputAnatomy, selectAnatomy } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers,
  defineStyle,
  defineStyleConfig,
} from "@chakra-ui/react";

const {
  definePartsStyle: defineInputParts,
  defineMultiStyleConfig: defineInputMultiStyleConfig,
} = createMultiStyleConfigHelpers(inputAnatomy.keys);

const {
  definePartsStyle: defineSelectParts,
  defineMultiStyleConfig: defineSelectMultiStyleConfig,
} = createMultiStyleConfigHelpers(selectAnatomy.keys);

const getAuthRoundedFieldStyle = (props, focusBorderColor, errorBorderColor) => ({
  bg: "inputBg",
  borderWidth: "1px",
  borderColor: "inputBorder",
  borderRadius: "full",
  color: "textPrimary",
  backdropFilter: "saturate(180%) blur(16px)",
  WebkitBackdropFilter: "saturate(180%) blur(16px)",
  transitionProperty: "background-color, border-color, color, box-shadow",
  transitionDuration: "var(--motion-duration-fast)",
  transitionTimingFunction: "var(--motion-ease-apple)",
  _placeholder: { color: "inputPlaceholder", opacity: 0.8 },
  _hover: {
    borderColor: "borderSecondary",
    bg: "inputBg",
  },
  _focusVisible: {
    bg: "bgCard",
    borderColor: focusBorderColor || "inputFocusBorder",
    boxShadow: "focusOutlineAccent",
    zIndex: 1,
  },
  _focus: {
    bg: "bgCard",
    borderColor: focusBorderColor || "inputFocusBorder",
    boxShadow: "focusOutlineAccent",
    zIndex: 1,
  },
  _invalid: {
    borderColor: errorBorderColor || "statusError",
    boxShadow: "focusOutlineError",
  },
  _disabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    bg: props.colorMode === "dark" ? "whiteAlpha.100" : "gray.100",
    borderColor: "borderPrimary",
  },
});

// AI Temalı Varsayılan Input (ve Select Field) Stili
const aiOutlineVariant = defineInputParts((props) => {
  const { focusBorderColor: fc, errorBorderColor: ec, theme } = props;
  return {
    field: {
      bg: "inputBg",
      borderWidth: "1px",
      borderColor: "inputBorder",
      borderRadius: "lg",
      color: "textPrimary",
      backdropFilter: "saturate(180%) blur(16px)",
      WebkitBackdropFilter: "saturate(180%) blur(16px)",
      transitionProperty: "background-color, border-color, color, box-shadow",
      transitionDuration: "var(--motion-duration-fast)",
      transitionTimingFunction: "var(--motion-ease-apple)",
      _placeholder: { color: "inputPlaceholder", opacity: 0.8 },
      _hover: { borderColor: "borderSecondary" },
      _focusVisible: {
        borderColor: fc || "inputFocusBorder",
        boxShadow: `0 0 0 1px ${fc || theme.colors.inputFocusBorder}`,
        zIndex: 1,
      },
      _invalid: {
        borderColor: ec || "statusError",
        boxShadow: `0 0 0 1px ${ec || theme.colors.statusError}`,
      },
      _disabled: {
        opacity: 0.5,
        cursor: "not-allowed",
        bg: props.colorMode === "dark" ? "whiteAlpha.100" : "gray.100",
        borderColor: "borderPrimary",
      },
    },
    addon: {
      bg: "bgTertiary",
      borderColor: "borderPrimary",
      borderRadius: "lg",
      color: "textSecondary",
    },
    element: {
      color: "textMuted",
      _hover: { color: "textPrimary" },
    },
  };
});

const authRoundedVariant = defineInputParts((props) => {
  const { focusBorderColor: fc, errorBorderColor: ec } = props;
  return {
    field: getAuthRoundedFieldStyle(props, fc, ec),
    addon: {
      bg: "bgTertiary",
      borderColor: "borderPrimary",
      borderRadius: "full",
      color: "textSecondary",
    },
    element: {
      color: "textMuted",
      _hover: { color: "textPrimary" },
    },
  };
});

const authRoundedSelectVariant = defineSelectParts((props) => {
  const { focusBorderColor: fc, errorBorderColor: ec } = props;
  return {
    field: getAuthRoundedFieldStyle(props, fc, ec),
    icon: {
      color: "textMuted",
      insetEnd: "0.5rem",
      fontSize: "1.25em",
    },
  };
});

const inputThemeBase = {
  baseStyle: defineInputParts({
    field: {
      width: '100%',
      minWidth: 0,
      fontSize: { base: '16px', md: 'md' },
    },
  }),
  variants: {
    outline: aiOutlineVariant,
    filled: defineInputParts((props) => ({
      field: {
        bg: "bgTertiary",
        borderColor: "transparent",
        borderRadius: "lg",
        color: "textPrimary",
        backdropFilter: "saturate(180%) blur(16px)",
        WebkitBackdropFilter: "saturate(180%) blur(16px)",
        transitionProperty: "background-color, border-color, color, box-shadow",
        transitionDuration: "var(--motion-duration-fast)",
        transitionTimingFunction: "var(--motion-ease-apple)",
        _hover: { bg: props.colorMode === "dark" ? "gray.600" : "gray.200" },
        _focusVisible: {
          bg: "inputBg",
          borderColor: "inputFocusBorder",
          boxShadow: `0 0 0 1px ${props.theme.colors.inputFocusBorder}`,
        },
        _invalid: {
          borderColor: "statusError",
          boxShadow: `0 0 0 1px ${props.theme.colors.statusError}`,
        },
      },
    })),
    authRounded: authRoundedVariant,
  },
  sizes: {
    lg: defineInputParts({
      field: {
        fontSize: 'lg',  // 17px her yerde (tutarlı)
        h: '12',  // 48px her yerde
        px: 4,
      },
    }),
    md: defineInputParts({
      field: {
        fontSize: { base: '16px', md: 'md' },
        h: '10',  // 40px her yerde
        px: 4,
      },
    }),
    sm: defineInputParts({
      field: {
        fontSize: { base: '16px', md: 'sm' },
        h: '8',  // 32px her yerde
        px: 3,
      },
    }),
    xs: defineInputParts({
      field: {
        // iOS Safari auto-zooms when focusing inputs with font-size < 16px.
        // Keep the visual xs size on desktop, force 16px on mobile to defeat the zoom.
        fontSize: { base: '16px', md: 'xs' },
        h: '6',  // 24px her yerde
        px: 2,
      },
    }),
  },
  defaultProps: {
    variant: "outline",
    size: "md",
    focusBorderColor: "inputFocusBorder",
    errorBorderColor: "statusError",
  },
};

export const Input = defineInputMultiStyleConfig(inputThemeBase);

export const Select = defineSelectMultiStyleConfig({
  ...inputThemeBase,
  variants: {
    ...inputThemeBase.variants,
    authRounded: authRoundedSelectVariant,
  },
  baseStyle: defineSelectParts({
    field: {
      width: '100%',
      minWidth: 0,
      // Mobilde 16px ile iOS auto-zoom engellenir
      fontSize: { base: '16px', md: 'md' },
      // Option'lar için de küçük font
      '& option': {
        fontSize: { base: '16px', md: 'md' },
        bg: 'inputBg',
        color: 'textPrimary',
      },
    },
    icon: {
      color: "textMuted",
      insetEnd: "0.5rem",
      fontSize: "1.25em",
    },
  }),
  sizes: {
    lg: defineSelectParts({
      field: {
        fontSize: 'lg',  // 17px her yerde (tutarlı)
        h: '12',  // 48px her yerde
        px: 4,
      },
    }),
    md: defineSelectParts({
      field: {
        fontSize: { base: '16px', md: 'md' },
        h: '10',  // 40px her yerde
        px: 4,
      },
    }),
    sm: defineSelectParts({
      field: {
        fontSize: { base: '16px', md: 'sm' },
        h: '8',  // 32px her yerde
        px: 3,
      },
    }),
    xs: defineSelectParts({
      field: {
        fontSize: { base: '16px', md: 'xs' },
        h: '6',  // 24px her yerde
        px: 2,
      },
    }),
  },
  defaultProps: {
    size: 'md',
  },
});

// Textarea için: defineStyleConfig kullanarak tek parçalı stil tanımı
export const Textarea = defineStyleConfig({
  variants: {
    outline: defineStyle((props) => inputThemeBase.variants.outline(props).field),
    filled: defineStyle((props) => inputThemeBase.variants.filled(props).field),
  },
  sizes: {
    lg: {
      fontSize: 'lg',  // 17px tutarlı
      px: 4,
      py: 3,
    },
    md: {
      fontSize: { base: '16px', md: 'md' },
      px: 4,
      py: 2.5,
    },
    sm: {
      fontSize: { base: '16px', md: 'sm' },
      px: 3,
      py: 2,
    },
    xs: {
      fontSize: { base: '16px', md: 'xs' },
      px: 2,
      py: 1.5,
    },
  },
  defaultProps: {
    ...inputThemeBase.defaultProps,
    size: 'md',
  },
});
